import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { saveQrAttribution } from '../utils/qrAttribution';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Postcard QR landing page. Lifecycle:
 *   1. Postcard QR encodes `theailedger.ai/qr/<slug>`.
 *   2. We POST `/api/qr/<slug>/scan` so the backend logs the scan with
 *      IP, country, UA, device class.
 *   3. We persist `tal_qr_attribution` in localStorage (slug + coupon
 *      code) so the rest of the session — and future visits within
 *      30 days — carry the attribution into signup + checkout.
 *   4. Hard-redirect to the homepage with `?qr=<slug>&code=POSTCARD10`
 *      so the welcome banner pops on arrival.
 *
 * Failure paths: an unknown / expired slug 404s on the API. We still
 * redirect to `/` (without the qr param) so the visitor doesn't land
 * on a dead page — just no banner, no attribution. The backend
 * already filters tampered slugs at signup/checkout, so frontend can
 * stay optimistic here.
 */
export default function QrPostcardRedirect() {
  const { slug } = useParams();

  useEffect(() => {
    let cancelled = false;
    const safe = (slug || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!safe) {
      window.location.replace('/');
      return () => {};
    }

    (async () => {
      let redirectTo = '/';
      try {
        const resp = await fetch(`${API_URL}/api/qr/${safe}/scan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data?.campaign_slug) {
            saveQrAttribution({ slug: data.campaign_slug, code: data.coupon_code });
          }
          if (data?.redirect_to) redirectTo = data.redirect_to;
        }
      } catch (_) {
        // network drop is fine — still bounce them to the homepage
      }
      if (!cancelled) window.location.replace(redirectTo);
    })();

    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center"
      data-testid="qr-postcard-redirect"
    >
      <div className="text-center space-y-3">
        <div className="w-10 h-10 border-4 border-[#FF8C00] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-neutral-400">Welcome from the postcard — loading your discount…</p>
      </div>
    </div>
  );
}
